/**
 * DENET — "sepete eklendi" onay penceresi
 *
 * Ürün kartındaki sepete ekle düz bir form gönderimiydi: tarayıcı sayfayı
 * terk edip /cart adresine gidiyordu. Müşteri gezindiği listeden koparılıyor,
 * geri dönüp yerini yeniden bulmak zorunda kalıyordu.
 *
 * Artık form yakalanıyor, ürün arka planda ekleniyor ve ne olduğunu anlatan
 * bir pencere açılıyor: ürün, fiyatı, sepetteki toplam adet ve iki yol —
 * alışverişe devam ya da sepete git. Sayfa hiç terk edilmiyor.
 *
 * Pencere sayfayı kilitlemiyor. Bir onay bildirimi için arkayı karartıp
 * kaydırmayı durdurmak, düzeltmek istediğimiz kesintinin aynısını yaratırdı;
 * müşteri pencereyi görmezden gelip gezinmeye devam edebilmeli.
 *
 * Kendi kendine kapanıyor (varsayılan 6 sn). Üzerine gelindiğinde ya da
 * içine odaklanıldığında sayaç duruyor — "Sepete git"e uzanırken pencere
 * kaybolmasın. Üst üste ekleme yapılırsa yeni pencere açılmıyor, açık olan
 * güncelleniyor ve süre sıfırlanıyor.
 *
 * JS çalışmazsa form olduğu gibi gönderilir; işlev korunur, yalnız pencere
 * çıkmaz.
 */

import { DenetElement, define, emit, formatMoney, EVENTS } from '@denet/core';

const root = () => window.Shopify?.routes?.root || '/';

class DnAdded extends DenetElement {
  mounted() {
    this.life = Number(this.dataset.life || 6000);
    /* Kuruş cinsinden; 0 ise çubuk hiç çizilmez. */
    this.threshold = Number(this.dataset.threshold || 0);
    this.timer = 0;
    this.count = 0;

    /* Sayfadaki tüm kart formları tek dinleyiciyle yakalanıyor: raylar ve
       filtreler kart ekleyip çıkarıyor, tek tek bağlamak kaçak veriyordu. */
    document.addEventListener('submit', (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.matches('form[data-cart-add]')) return;
      event.preventDefault();
      this.#add(form);
    }, { signal: this.signal });

    this.addEventListener('click', (event) => {
      if (event.target instanceof Element && event.target.closest('[data-close]')) this.close();
    }, { signal: this.signal });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !this.hidden) this.close();
    }, { signal: this.signal });

    /* Okumaya ya da düğmeye uzanmaya zaman tanı. */
    this.addEventListener('pointerenter', () => clearTimeout(this.timer), { signal: this.signal });
    this.addEventListener('focusin', () => clearTimeout(this.timer), { signal: this.signal });
    this.addEventListener('pointerleave', () => this.#arm(), { signal: this.signal });
  }

  /** @param {HTMLFormElement} form */
  async #add(form) {
    const button = form.querySelector('button[type="submit"]');
    button?.setAttribute('aria-busy', 'true');

    try {
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!res.ok) {
        /* Shopify sebebi gövdede açıklıyor (stok yok, geçersiz varyant). */
        const body = await res.json().catch(() => null);
        throw new Error(body?.description || body?.message || String(res.status));
      }

      /* Sepetin tamamını ayrıca soruyoruz: add.js yalnız eklenen kalemi döner,
         penceredeki "sepette N ürün" bilgisi oradan gelmiyor. */
      const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .catch(() => null);

      /* silent: başlıktaki sepet sayısı güncellensin ama ayrıca bildirim
         çıkmasın — aynı bilgiyi pencere zaten veriyor. */
      emit(EVENTS.cartUpdated, { itemCount: cart?.item_count, silent: true });
      emit('denet:eklendi', { button });
      this.open(form.dataset, cart);
    } catch (error) {
      emit(EVENTS.cartError, { message: error instanceof Error ? error.message : undefined });
    } finally {
      button?.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {DOMStringMap} data
   * @param {{ item_count?: number } | null} cart
   */
  open(data, cart) {
    const set = (name, value) => {
      const el = this.ref(name);
      if (el) el.textContent = value;
    };

    const image = this.ref('image');
    if (image instanceof HTMLImageElement) {
      if (data.image) { image.src = data.image; image.hidden = false; }
      else image.hidden = true;
    }

    set('title', data.title || '');
    set('price', data.price || '');

    const link = this.ref('view');
    if (link instanceof HTMLAnchorElement && data.url) link.href = data.url;

    this.count = this.hidden ? 1 : this.count + 1;
    const repeat = this.ref('repeat');
    if (repeat) {
      repeat.hidden = this.count < 2;
      repeat.textContent = (this.dataset.repeat || 'Art arda {n} ürün eklendi').replace('{n}', String(this.count));
    }

    if (cart?.item_count != null) {
      set('count', (this.dataset.incart || 'Sepette {n} ürün').replace('{n}', String(cart.item_count)));
    }

    this.#ship(cart?.total_price);

    this.hidden = false;
    this.#arm();
  }

  /**
   * Kargo eşiği çubuğu. Eklemenin hemen ardından "şu kadar daha ekle"
   * demenin en etkili anı burası; müşteri zaten alışveriş kararı vermiş
   * durumda ve sepete gitmeden görüyor.
   * @param {number | undefined} total kuruş cinsinden sepet toplamı
   */
  #ship(total) {
    const box = this.ref('ship');
    if (!box) return;
    if (!this.threshold || total == null) { box.hidden = true; return; }

    const remaining = this.threshold - total;
    const pct = Math.min(100, Math.round((total * 100) / this.threshold));
    const text = this.ref('shipText');
    const fill = this.ref('shipFill');

    box.hidden = false;
    box.classList.toggle('is-done', remaining <= 0);
    if (fill) fill.style.width = `${pct}%`;
    if (text) {
      text.textContent = remaining > 0
        ? (this.dataset.shipLeft || '{amount} daha ekleyin, kargo bedava').replace('{amount}', formatMoney(remaining))
        : (this.dataset.shipFree || 'Kargo bedava');
    }
  }

  #arm() {
    clearTimeout(this.timer);
    if (this.life > 0) this.timer = window.setTimeout(() => this.close(), this.life);
  }

  close() {
    if (this.hidden) return;
    clearTimeout(this.timer);
    this.hidden = true;
    this.count = 0;
  }
}

define('dn-added', DnAdded);
